/* eslint-disable no-unused-vars */

import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import TypeProtectedRoute from "./routes/TypeProtectedRoute";
import SubscriptionProtectedRoute from "./routes/SubscriptionProtectedRoute";
import { eoRoutes } from "./routes/eoRoutes";
import { mpsRoutes } from "./routes/mpsRoutes";
import Login from "./pages/Login/Login";
import CreateAccount from "./pages/CreateAccount/CreateAccount";
import MPSSignUpForm from "./components/SignUpForm/MPSSignUpForm/MPSSignUpForm";
import EOPaywall from "./pages/EOPaywall/EOPaywall";
import SubscriptionSuccess from "./pages/SubscriptionSuccess/SubscriptionSuccess";
import About from "./pages/FooterPages/About";
import Careers from "./pages/FooterPages/Careers";
import Directory from "./pages/FooterPages/Directory";
import Partnerships from "./pages/FooterPages/Partnerships";
import FAQs from "./pages/FooterPages/FAQs";
import Host from "./pages/FooterPages/Host";
import Contact from "./pages/FooterPages/Contact";
import Invest from "./pages/FooterPages/Invest";
import Support from "./pages/FooterPages/Support";
import ClimateAction from "./pages/FooterPages/ClimateAction";
import BrowseEvents from "./pages/BrowseEvents/BrowseEvents";
import EventPage from "./pages/EventPage/EventPage";
import TicketSuccess from "./pages/EventPage/TicketSuccess";
import CheckIn from "./pages/CheckIn/CheckIn";
import VendorCheckIn from "./pages/CheckIn/VendorCheckIn";
import TermsOfUse from "./pages/Legal/TermsOfUse";
import PrivacyPolicy from "./pages/Legal/PrivacyPolicy";
import AuthErrorPage from "./pages/AuthError/AuthErrorPage";
import EOCompleteProfile from "./pages/EOCompleteProfile/EOCompleteProfile";
import AuthCallback from "./pages/AuthCallback/AuthCallback";
import StripeConnectCallback from "./components/Stripe/StripeConnectCallback";
import DOMAIN_CONFIG from "./config/domains";
import GlowingOrb from "./components/ui/GlowingOrb";
import EORedirectHandler from "./components/EORedirectHandler";

const AppRouter = () => {
  const {
    user,
    userType,
    hasActiveSubscription,
    subscriptionStatus,
    crossDomainAuthPending,
    initialAuthCheckDone,
  } = useAuth();
  const isAuthDomainOnly = DOMAIN_CONFIG.shouldShowAuthOnly();

  if (isAuthDomainOnly) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<CreateAccount />} />
        <Route path="/mps-signup" element={<MPSSignUpForm />} />
        <Route path="/eo-paywall" element={<EOPaywall />} />
        <Route path="/subscription-success" element={<SubscriptionSuccess />} />
        <Route path="/eo/complete-profile" element={<EOCompleteProfile />} />
        <Route
          path="/stripe-connect-callback"
          element={<StripeConnectCallback />}
        />
        <Route path="/browse-events" element={<BrowseEvents />} />
        <Route path="/about" element={<About />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faqs" element={<FAQs />} />
        <Route path="/partnerships" element={<Partnerships />} />
        <Route path="/host" element={<Host />} />
        <Route path="/invest" element={<Invest />} />
        <Route path="/support" element={<Support />} />
        <Route path="/climate-action" element={<ClimateAction />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/auth-error" element={<AuthErrorPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<CreateAccount />} />
      <Route path="/browse-events" element={<BrowseEvents />} />
      <Route path="/events/:eventId" element={<EventPage />} />
      <Route path="/events/:eventId/success" element={<TicketSuccess />} />
      <Route
        path="/eo/events/:id/checkin"
        element={
          <TypeProtectedRoute userType="EO">
            <CheckIn />
          </TypeProtectedRoute>
        }
      />
      <Route
        path="/eo/events/:id/vendor-checkin"
        element={
          <TypeProtectedRoute userType="EO">
            <VendorCheckIn />
          </TypeProtectedRoute>
        }
      />
      <Route path="/auth-error" element={<AuthErrorPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/stripe-connect-callback"
        element={<StripeConnectCallback />}
      />

      {/* Footer pages - public routes */}
      <Route path="/about" element={<About />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/directory" element={<Directory />} />
      <Route path="/partnerships" element={<Partnerships />} />
      <Route path="/faqs" element={<FAQs />} />
      <Route path="/host" element={<Host />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/invest" element={<Invest />} />
      <Route path="/support" element={<Support />} />
      <Route path="/climate-action" element={<ClimateAction />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />

      {/* Sign up form routes */}
      <Route
        path="/mps-signup"
        element={
          <TypeProtectedRoute userType="MPS">
            <MPSSignUpForm />
          </TypeProtectedRoute>
        }
      />

      {/* Subscription Paywall and Success Routes */}
      <Route
        path="/eo-paywall"
        element={
          <TypeProtectedRoute userType="EO">
            <EOPaywall />
          </TypeProtectedRoute>
        }
      />
      <Route
        path="/subscription-success"
        element={
          <TypeProtectedRoute userType="EO">
            <SubscriptionSuccess />
          </TypeProtectedRoute>
        }
      />
      <Route
        path="/eo/complete-profile"
        element={
          <TypeProtectedRoute userType="EO">
            <EOCompleteProfile />
          </TypeProtectedRoute>
        }
      />

      {/* EO routes - Protected by subscription */}
      {eoRoutes.map(({ path, element }) => (
        <Route
          key={path}
          path={path}
          element={
            <TypeProtectedRoute userType="EO">
              <SubscriptionProtectedRoute>{element}</SubscriptionProtectedRoute>
            </TypeProtectedRoute>
          }
        />
      ))}

      {/* MPS routes */}
      {mpsRoutes.map(({ path, element }) => (
        <Route
          key={path}
          path={path}
          element={
            <TypeProtectedRoute userType="MPS">{element}</TypeProtectedRoute>
          }
        />
      ))}

      {/* Root redirect - wait for auth to complete before deciding */}
      <Route
        path="/"
        element={
          !initialAuthCheckDone || crossDomainAuthPending ? (
            <div className="min-h-screen flex items-center justify-center bg-white">
              <GlowingOrb size="md" />
            </div>
          ) : user ? (
            userType ? (
              userType === "EO" ? (
                <EORedirectHandler />
              ) : (
                <Navigate to="/mps/dashboard" replace />
              )
            ) : (
              <div className="min-h-screen flex items-center justify-center bg-white">
                <GlowingOrb size="md" />
              </div>
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRouter;
